import axios from 'axios';
import Axios from "axios";
import { baseUrl } from "./baseUrl";
import { uploadImage, updateImage } from './imageApiV2';

// Function to get the current token dynamically
const getToken = () => localStorage.getItem('cc-t') || sessionStorage.getItem('cc-t');

const api = Axios.create({
    baseURL: baseUrl,
});

// Add request interceptor to dynamically add the token to every request
api.interceptors.request.use(
    (config) => {
        const token = getToken();
        console.log('🔐 Request Interceptor - URL:', config.url);
        console.log('🔐 Request Interceptor - Token:', token ? 'Token exists (length: ' + token.length + ')' : 'No token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        config.headers['Content-Type'] = 'application/json';
        console.log('🔐 Request Interceptor - Headers:', config.headers);
        return config;
    },
    (error) => {
        console.error('🔐 Request Interceptor - Error:', error);
        return Promise.reject(error);
    }
);

// Create End User
export const createUser = async (data, image, toast) => {
    try {
        toast.loading('Creating User...');

        // Upload to Image API V2 first (if image provided)
        if (image) {
            toast.loading('Uploading profile picture...');
            const imageResult = await uploadImage(image, 'users', false);

            // Add Image API V2 metadata to user data
            data.profilePicId = imageResult.imageId;
            data.profilePicVersion = imageResult.version;
            data.profilepic = imageResult.url;

            console.log('✅ Profile picture uploaded:', imageResult);
        }

        // Create user with image metadata
        toast.loading('Creating user...');
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/create/user`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            },
            data: data
        };
        const res = await axios.request(config);

        if (res.data.success) {
            toast.dismiss();
            toast.success(res.data.message);
        }
        if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        console.log(err);
        toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

export const addUser = async (data) => {
    try {
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/create/user`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            },
            data: data
        };
        const res = await axios.request(config);
        return res.data;
    } catch (err) {
        console.log(err);
        return err;
    }
}

export const getUserById = async (id) => {
    try {
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/details/${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            },
        };
        const res = await axios.request(config);
        return res.data.user;
    } catch (err) {
        console.log(err);
        return null;
    }
}

// Create Green Ambassador
export const createAmbassador = async (data, image, toast) => {
    try {
        toast.loading('Creating Green Ambassador...');

        // Upload to Image API V2 first (if image provided)
        if (image && image instanceof File) {
            toast.loading('Uploading profile picture...');
            const imageResult = await uploadImage(image, 'ambassadors', false);

            // Add Image API V2 metadata to ambassador data
            data.profilePicId = imageResult.imageId;
            data.profilePicVersion = imageResult.version;
            data.profilepic = imageResult.url;

            console.log('✅ Ambassador profile picture uploaded:', imageResult);
        }

        // Create ambassador with image metadata
        toast.loading('Creating Green Ambassador...');
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/create/green-ambassador`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            },
            data: data
        };
        const res = await axios.request(config);
        console.log(res);

        if (res.data.success) {
            toast.dismiss();
            toast.success(res.data.message);
        }
        if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
            return res.data.error;
        }
    } catch (err) {
        toast.dismiss();
        toast.error('Something Went Wrong!');
        console.log('error creating green ambassador: ', err);
        return err;
    }
}

// Create NGO
export const createNgo = async (data, image, toast) => {
    try {
        toast.loading('Creating NGO...');

        // Upload to Image API V2 first (if image provided)
        if (image) {
            toast.loading('Uploading NGO logo...');
            const imageResult = await uploadImage(image, 'ngo-logos', false);

            // Add Image API V2 metadata to NGO data
            data.logoId = imageResult.imageId;
            data.logoVersion = imageResult.version;
            data.logo = imageResult.url;

            console.log('✅ NGO logo uploaded:', imageResult);
        }

        // Create NGO with image metadata
        toast.loading('Creating NGO...');
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/create/ngo`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            },
            data: data
        };
        const res = await axios.request(config);

        if (res.data.success) {
            toast.dismiss();
            toast.success(res.data.message);
        }
        if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message || res.data?.error);
        }
    } catch (err) {
        toast.dismiss();
        const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Something Went Wrong!';
        toast.error(errorMessage);
    }
};

// Create Company
export const createCompany = async (data, image, navigate, toast) => {
    try {
        toast.loading('Creating Company...');

        // Upload to Image API V2 first (if image provided)
        if (image) {
            toast.loading('Uploading company logo...');
            const imageResult = await uploadImage(image, 'company-logos', false);

            // Add Image API V2 metadata to company data
            data.logoId = imageResult.imageId;
            data.logoVersion = imageResult.version;
            data.logo = imageResult.url;

            console.log('✅ Company logo uploaded:', imageResult);
        }

        // Create company with image metadata
        toast.loading('Creating company...');
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/create/company`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            },
            data: data
        };
        const res = await axios.request(config);
        console.log(res.data);

        if (res.data.success) {
            toast.dismiss();
            toast.success(res.data.message);
            setTimeout(() => {
                navigate('/user/list/companies');
            }, 2000);
        }
        if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        console.log(err.response.data.error);
        toast.dismiss();
        toast.error(err.response.data.error ?? 'Something Went Wrong!');
    }
}

// Get All Users List (with pagination support)
export const getUsersList = async (toast, userType, filters = {}) => {
    try {
        // Add default pagination if not provided
        const filtersWithPagination = {
            page: 1,
            limit: 50,
            ...filters, // User-provided filters can override defaults
        };

        console.log('🔍 getUsersList - Request:', { userType, filters: filtersWithPagination });
        const res = await api.post(`/admin/user/list/user?userType=${userType || "user"}`, filtersWithPagination);
        console.log('✅ getUsersList - Response:', res.data);

        if (res.data.success) {
            toast.dismiss();
            const users = res.data.users.map(user => {
                user.active = user.active.toString();
                return user;
            });
            return {
                users,
                pagination: res.data.pagination || null,
            };
        }
        if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
            return null;
        }
    } catch (err) {
        console.error('❌ getUsersList - Error:', err);
        console.error('❌ getUsersList - Error response:', err.response?.data);
        console.error('❌ getUsersList - Error status:', err.response?.status);
        toast.dismiss();
        const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Something Went Wrong!';
        toast.error(errorMessage);
        return null;
    }
}

// Get All Users(user/ambassador) List
export const getAllUserList = async (toast) => {
    try {
        const res = await api.post('/admin/user/list/user');

        if (res.data.success) {
            const users = res.data.users.map(user => {
                user.active = user.active.toString()
                return user;
            });
            return users;
        }
        if (res.data?.error) {
            // toast.error(res.data?.error?.message);
            toast.error('Failed to fetch users!');
        }
    } catch (err) {
        // toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// Get All NGOs List (with pagination support)
export const getNgoList = async (setUsers, toast, filters = {}) => {
    try {
        // Add default pagination if not provided
        const filtersWithPagination = {
            page: 1,
            limit: 50,
            ...filters, // User-provided filters can override defaults
        };

        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/list/ngo`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            },
            data: filtersWithPagination
        };
        const res = await axios.request(config);
        if (res.data.success) {
            const u = (res.data.ngos || res.data.data || []).map(ngo => {
                ngo.active = ngo.active?.toString?.() ?? String(ngo.active);
                return ngo;
            });
            setUsers({
                ngos: u,
                pagination: res.data.pagination || null,
            });
        }
        if (res.data?.error) {
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// Get All Company List (with pagination support)
export const getCompanyList = async (setUsers, toast, filters = {}) => {
    try {
        // Add default pagination if not provided
        const filtersWithPagination = {
            page: 1,
            limit: 50,
            ...filters, // User-provided filters can override defaults
        };

        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/list/company`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            },
            data: filtersWithPagination
        };
        const res = await axios.request(config);
        if (res.data.success) {
            const u = res.data.companies.map(company => {
                company.active = company.active.toString()
                return company;
            });
            setUsers({
                companies: u,
                pagination: res.data.pagination || null,
            });
        }
        if (res.data?.error) {
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// Get Single User Details To Update
export const getUserDetails = async (id, socialFields, setSocialFields, locationFields, setLocationFields, personalFields, setPersonalFields, setImage, setActive, toast) => {
    try {
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/details/${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            },
        };
        const res = await axios.request(config);
        if (res.data.success) {
            const updatedSocialFields = socialFields.map(field => ({
                ...field, value: res.data.user.socialLinks[field.name] || ''
            }));
            setSocialFields(updatedSocialFields);
            const updatedLocationFields = locationFields.map(field => ({
                ...field, value: res.data.user.location[field.name] || ''
            }));
            setLocationFields(updatedLocationFields);
            const updatedPersonalFields = personalFields.map(field => {
                if (field.name === 'referredBy' || field.name === 'affiliatedBy') {
                    // Handle referredBy and affiliatedBy specifically
                    const refObj = res.data.user[field.name];
                    // Prefer .userId if present, then _id, then raw value
                    const value = refObj?.userId || refObj?._id || refObj || '';
                    const displayValue = refObj?.name || refObj?.username || '';
                    return { ...field, value, displayValue };
                }
                // Handle referredByCode - extract from referredBy.referralCode
                if (field.name === 'referredByCode') {
                    const referralCode = res.data.user.referredBy?.referralCode || '';
                    return { ...field, value: referralCode };
                }
                return {
                    ...field,
                    value: (typeof res.data.user[field.name] === 'object' ? res.data.user[field.name][field.name] : res.data.user[field.name]) || res.data.user.name[field.name] || ''
                };
            });
            setPersonalFields(updatedPersonalFields);
            setActive(res.data.user.active);
            setImage(res.data.user.profilepic);
        }
        if (res.data?.error) {
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        console.log(err);
        toast.error('Something Went Wrong!');
    }
}

// Get Single Ambassador User Details To Update
export const getAmbassadorDetails = async (id, personalFields, setPersonalFields, socialFields, setSocialFields, locationFields, setLocationFields, ambassadorDetails, setAmbassadorDetails, setImage, setActive, toast) => {
    try {
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/details/${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            },
        };
        const res = await axios.request(config);
        if (res.data.success) {
            const updatedPersonalFields = personalFields.map(field => ({
                ...field, value: res.data.user[field.name] || res.data.user.name[field.name] || ''
            }));
            setPersonalFields(updatedPersonalFields);
            const updatedSocialFields = socialFields.map(field => ({
                ...field, value: res.data.user.socialLinks[field.name] || ''
            }));
            setSocialFields(updatedSocialFields);
            const updatedLocationFields = locationFields.map(field => ({
                ...field, value: res.data.user.location[field.name] || ''
            }));
            setLocationFields(updatedLocationFields);
            const updatedAmbassadorDetails = ambassadorDetails.map(field => ({
                ...field, value: res.data.user.ambassadorDetails[field.name] || ''
            }));
            setAmbassadorDetails(updatedAmbassadorDetails);
            setActive(res.data.user.active);
            setImage(res.data.user.profilepic);
        }
        if (res.data?.error) {
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        console.log(err);
        toast.error('Something Went Wrong!');
    }
}

export const getAmbassador = async (id) => {
    try {
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/details/${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            },
        };
        const res = await axios.request(config);
        if (res.data.success) {
            return res.data.ambassador || {};
        }
    } catch (err) {
        console.log(err);
    }
}

// Get Single NGO Details To Update
export const getNgoDetails = async (id, formData, setFormData, socialFields, setSocialFields, pocFields, setPocFields, accountFields, setAccountFields, organizationalFields, setOrganizationalFields, partnershipFields, setPartnershipFields, complianceFields, setComplianceFields, impactFields, setImpactFields, setImage, toast) => {
    try {
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/ngo/details/${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            },
        };
        const res = await axios.request(config);
        if (res.data.success) {
            const updatedFields = formData.map(field => ({
                ...field, value: res.data.ngo[field.name] || '',
            }));
            setFormData(updatedFields);
            const updatedSocialFields = socialFields.map(field => ({
                ...field, value: res.data.ngo.socialLinks[field.name] || ''
            }));
            setSocialFields(updatedSocialFields);
            const updatedPocFields = pocFields.map(field => ({
                ...field, value: res.data.ngo.pointOfContact[field.name] || ''
            }));
            setPocFields(updatedPocFields);
            const updatedAccountFields = accountFields.map(field => ({
                ...field, value: res.data.ngo.accountDetails[field.name] || ''
            }));
            setAccountFields(updatedAccountFields);
            const updatedOrgFields = organizationalFields.map(field => ({
                ...field, value: res.data.ngo.organizationalDetails[field.name] || ''
            }));
            setOrganizationalFields(updatedOrgFields);
            const updatedPartnerFields = partnershipFields.map(field => ({
                ...field, value: res.data.ngo.partnershipDetails[field.name] || ''
            }));
            setPartnershipFields(updatedPartnerFields);
            const updatedComplianceFields = complianceFields.map(field => ({
                ...field, value: res.data.ngo.complianceAndAgreements[field.name] || ''
            }));
            setComplianceFields(updatedComplianceFields);
            const updatedImpactFields = impactFields.map(field => ({
                ...field, value: res.data.ngo.impactAndReporting[field.name] || ''
            }));
            setImpactFields(updatedImpactFields);

            setImage(res.data.ngo.logo);
        }
        if (res.data?.error) {
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        console.log(err);
        toast.error('Something Went Wrong!');
    }
}

export const fetchNgoDetails = async (id) => {
    try {
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/ngo/details/${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            },
        };
        const res = await axios.request(config);
        return res.data.ngo;
    } catch (error) {
        console.log(error);
    }
}

// Get Single Company Details To Update
export const getCompanyDetails = async (id, formData, setFormData, socialFields, setSocialFields, accountFields, setAccountFields, pocFields, setPocFields, partnerFields, setPartnerFields, targetUserFields, setTargetUserFields, financialFields, setFinancialFields, impactFields, setImpactFields, setImage, toast) => {
    try {
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/company/details/${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            },
        };
        const res = await axios.request(config);
        if (res.data.success) {
            const updatedFields = formData.map(field => ({
                ...field, value: res.data.company[field.name] || '',
            }));
            setFormData(updatedFields);
            const updatedSocialFields = socialFields.map(field => ({
                ...field, value: res.data.company.socialLinks[field.name] || ''
            }));
            setSocialFields(updatedSocialFields);
            const updatedPocFields = pocFields.map(field => ({
                ...field, value: res.data.company.pointOfContact[field.name] || ''
            }));
            setPocFields(updatedPocFields);
            const updatedAccountFields = accountFields.map(field => ({
                ...field, value: res.data.company.accountDetails[field.name] || ''
            }));
            setAccountFields(updatedAccountFields);
            const updatedPartnerFields = partnerFields.map(field => ({
                ...field, value: res.data.company.partnershipDetails[field.name] || ''
            }));
            setPartnerFields(updatedPartnerFields);
            const updatedTargetFields = targetUserFields.map(field => ({
                ...field, value: res.data.company.targetUserAgeGroupPartnershipObjectives[field.name] || ''
            }));
            setTargetUserFields(updatedTargetFields);
            const updatedFinancialFields = financialFields.map(field => ({
                ...field, value: res.data.company.financialDetails[field.name] || ''
            }));
            setFinancialFields(updatedFinancialFields);
            const updatedImpactFields = impactFields.map(field => ({
                ...field, value: res.data.company.impactAndReporting[field.name] || ''
            }));
            setImpactFields(updatedImpactFields);

            setImage(res.data.company.logo);
        }
        if (res.data?.error) {
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        console.log(err);
        toast.error('Something Went Wrong!');
    }
}

export const fetchCompanyDetails = async (id) => {
    let config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: `${baseUrl}/admin/user/company/details/${id}`,
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getToken()}`
        },
    };
    const res = await axios.request(config);
    console.log('Fetched company details:', res.data);
    return res.data?.company;
}

export const savePartnerAndSendCredentials = async (data) => {
    try {
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/send-partner-credentials`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            },
            data: data
        };
        const res = await axios.request(config);
        return res.data;
    } catch (error) {
        console.error(error);
        throw new Error('Failed to save partner and send credentials');
    }
}

export const sendAmbassadorCredentials = async (data) => {
    try {
        console.log('Sending ambassador credentials with data:', data);
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/send-ambassador-credentials`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            },
            data: data
        };
        const res = await axios.request(config);
        return res.data;
    } catch (error) {
        console.error(error);
        throw new Error('Failed to send ambassador credentials');
    }
}

// Get Single User Details
export const getUserDetailsView = async (id, setUserDetails, toast) => {
    try {
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/details/${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            },
        };
        const res = await axios.request(config);
        if (res.data.success) {
            setUserDetails(res.data.user);
        }
        if (res.data?.error) {
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        console.log(err);
        toast.error('Something Went Wrong!');
    }
}

// Update User
export const updateUser = async (id, data, image, toast, navigate) => {
    try {
        toast.loading('Updating User...');
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/update/user/${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            },
            data: data
        };
        const res = await axios.request(config);
        if (res.data.success) {
            // If server returned an upload URL and frontend provided a new File, upload it to S3 (presigned URL)
            // Only perform client-side PUT upload when the frontend did NOT send base64 `profilepic`.
            // If `profilepic` was included in the JSON payload, the server likely handled the upload.
            if (res.data.uploadUrl && data.isImagePresent && !data.profilepic && image instanceof Blob) {
                try {
                    const imgConfig = {
                        method: 'put',
                        maxBodyLength: Infinity,
                        url: res.data.uploadUrl,
                        headers: {
                            'Content-Type': image.type || 'application/octet-stream',
                        },
                        data: image
                    };
                    const imgUpload = await axios.request(imgConfig);
                    imgUpload && toast.dismiss();
                    imgUpload && toast.success(res.data.message);
                } catch (imgErr) {
                    console.error('Image upload failed', imgErr);
                    toast.dismiss();
                    toast.error('User updated but image upload failed');
                    return;
                }
            }
            toast.dismiss();
            toast.success(res.data.message);
            // Navigate back to user list after successful update
            if (navigate) {
                setTimeout(() => {
                    navigate('/user/list/app-users');
                }, 1500);
            }
        }
        if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        console.log(err);
        toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// Update Green Ambassador
export const updateAmbassador = async (id, data, image, toast) => {
    try {
        toast.loading('Updating Ambassador...');

        // Upload to Image API V2 if new image provided
        if (image && data.isImagePresent && image instanceof File) {
            toast.loading('Uploading profile picture...');

            // Use updateImage if ambassador already has profilePicId, otherwise upload new
            let imageResult;
            if (data.profilePicId) {
                imageResult = await updateImage(data.profilePicId, image);
                console.log('✅ Ambassador profile picture updated to version:', imageResult.version);
            } else {
                imageResult = await uploadImage(image, 'ambassadors', false);
                console.log('✅ Ambassador profile picture uploaded:', imageResult);
            }

            // Add Image API V2 metadata
            data.profilePicId = imageResult.imageId;
            data.profilePicVersion = imageResult.version;
            data.profilepic = imageResult.url;
        }

        // Update ambassador with image metadata
        toast.loading('Updating Ambassador...');
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/update/ambassador/${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            },
            data: data
        };
        const res = await axios.request(config);

        if (res.data.success) {
            toast.dismiss();
            toast.success(res.data.message);
        }
        if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        console.log(err);
        toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// Update ambassador NEW ROUTE
export const updateGreenAmbassador = async (id, data) => {
    try {
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/update/ambassador/${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            },
            data: data
        };
        const res = await axios.request(config);
        if (res.data.success) {
            return res.data.message;
        }
    } catch (err) {
        console.log(err);
    }
}

// Update NGO
export const updateNgo = async (id, data, image, toast) => {
    try {
        toast.loading('Updating NGO...');

        // Upload to Image API V2 if new image provided (File object, not URL string)
        if (image && image instanceof File) {
            toast.loading('Uploading NGO logo...');

            // Use updateImage if NGO already has logoId, otherwise upload new
            let imageResult;
            if (data.logoId) {
                imageResult = await updateImage(data.logoId, image);
                console.log('✅ NGO logo updated to version:', imageResult.version);
            } else {
                imageResult = await uploadImage(image, 'ngo-logos', false);
                console.log('✅ NGO logo uploaded:', imageResult);
            }

            // Add Image API V2 metadata
            data.logoId = imageResult.imageId;
            data.logoVersion = imageResult.version;
            data.logo = imageResult.url;
        }

        // Update NGO with image metadata
        toast.loading('Updating NGO...');
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/update/ngo/${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            },
            data: data
        };
        const res = await axios.request(config);

        if (res.data.success) {
            toast.dismiss();
            toast.success(res.data.message);
        }
        if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        console.log(err);
        toast.dismiss();
        const errorMessage = err.response?.data?.error?.message || err.response?.data?.message || 'Something Went Wrong!';
        toast.error(errorMessage);
    }
}

// Update Company
export const updateCompany = async (id, data, image, navigate, toast) => {
    try {
        toast.loading('Updating Company...');

        // Upload to Image API V2 if new image provided (File object, not URL string)
        if (image && image instanceof File) {
            toast.loading('Uploading company logo...');

            // Use updateImage if company already has logoId, otherwise upload new
            let imageResult;
            if (data.logoId) {
                imageResult = await updateImage(data.logoId, image);
                console.log('✅ Company logo updated to version:', imageResult.version);
            } else {
                imageResult = await uploadImage(image, 'company-logos', false);
                console.log('✅ Company logo uploaded:', imageResult);
            }

            // Add Image API V2 metadata
            data.logoId = imageResult.imageId;
            data.logoVersion = imageResult.version;
            data.logo = imageResult.url;
        }

        // Update company with image metadata
        toast.loading('Updating company...');
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/update/company/${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            },
            data: data
        };
        const res = await axios.request(config);

        if (res.data.success) {
            toast.dismiss();
            toast.success(res.data.message);
            setTimeout(() => {
                navigate('/user/list/companies');
            }, 2000);
        }
        if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        console.log(err);
        toast.dismiss();
        const errorMessage = err.response?.data?.error?.message || err.response?.data?.message || 'Something Went Wrong!';
        toast.error(errorMessage);
    }
}

// Delete User
export const deleteUser = async (id, toast) => {
    try {
        toast.loading('Deleting User...');
        let config = {
            method: 'delete',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/delete/user/${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            },
        };
        const res = await axios.request(config);
        if (res.data.success) {
            toast.dismiss();
            toast.success(res.data.message);
        }
        if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        toast.error("Something Went Wrong!");
    }
}

// Delete NGO
export const deleteNgo = async (id, toast) => {
    try {
        toast.loading('Deleting NGO...');
        let config = {
            method: 'delete',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/delete/ngo/${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            },
        };
        const res = await axios.request(config);
        if (res.data.success) {
            toast.dismiss();
            toast.success(res.data.message);
        }
        if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        toast.error("Something Went Wrong!");
    }
}

// Delete Company
export const deleteCompany = async (id, toast) => {
    try {
        toast.loading('Deleting NGO...');
        let config = {
            method: 'delete',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/delete/company/${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            },
        };
        const res = await axios.request(config);
        console.log(res.data);
        if (res.data.success) {
            toast.dismiss();
            toast.success(res.data.message);
        }
        if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        toast.error("Something Went Wrong!");
    }
}

// User Points Allocation Logs
export const userPointsAllocationLogs = async (toast) => {
    try {
        const res = await api.post("/admin/user/point-allocation-logs");
        if (res.data.success) {
            return res.data.logs;
        }
        else toast.error(res.data?.error?.message);
    } catch (error) {
        console.log(error);
    }
}

// Get organizers list
export const getOrganizersList = async () => {
    try {
        const res = await api.post("/admin/user/list/organizers");
        if (res.data.success) {
            const list = res.data.organizers.map(organizer => {
                const name = organizer.name;
                organizer.displayName = `<b>${organizer.organiserType}</b>: ${name}`;
                organizer.name = `${organizer.organiserType}: ${name}`;
                return organizer;
            });
            return list;
        }
    } catch (error) {
        console.log(error);
    }
}

// get user deletion requests
export const getUserDeletionRequests = async () => {
    try {
        const res = await api.get("/admin/user/user-deletion-requests");
        if (res.data.success) {
            return res.data.data;
        }
        return [];
    } catch (error) {
        console.log(error);
    }
}

// get user deletion requests details
export const getUserDeletionRequestDetails = async (id) => {
    try {
        const res = await api.get(`/admin/user/user-deletion-requests/${id}`);
        if (res.data.success) {
            return res.data.data;
        }
        return [];
    } catch (error) {
        console.log(error);
    }
}

// resolve user deletion request
export const resolveUserDeletionRequest = async (data) => {
    try {
        const res = await api.post("/admin/user/user-deletion-requests/resolve", data);
        return res.data;
    } catch (error) {
        console.log(error);
    }
}

// Get deleted users list
export const getDeletedUsers = async (params = {}) => {
    try {
        console.log('🔍 getDeletedUsers - Token check:', getToken() ? 'Token exists' : 'No token');
        console.log('🔍 getDeletedUsers - Making request to:', '/admin/user/deleted-users');
        const res = await api.get("/admin/user/deleted-users", { params });
        console.log('✅ getDeletedUsers - Response:', res.data);
        if (res.data.success) {
            return res.data.data;
        }
        return [];
    } catch (error) {
        console.error('❌ getDeletedUsers - Error:', error);
        console.error('❌ getDeletedUsers - Error response:', error.response?.data);
        console.error('❌ getDeletedUsers - Error status:', error.response?.status);
        console.error('❌ getDeletedUsers - Request headers:', error.config?.headers);
        return [];
    }
}

// Get deleted user details
export const getDeletedUserDetails = async (id) => {
    try {
        const res = await api.get(`/admin/user/deleted-users/${id}`);
        if (res.data.success) {
            return res.data.data;
        }
        return null;
    } catch (error) {
        console.log(error);
        return null;
    }
}

// Get users for dropdown filters (username list)
export const getUsersForFilters = async () => {
    try {
        // backend mounts challenge routes under `/admin/challenges` (plural)
        const res = await api.post('/admin/challenges/get-users-for-filters');
        if (res.data.success) {
            return res.data.users;
        }
        return [];
    } catch (err) {
        console.error('Failed to fetch users for filters', err?.response?.data || err.message || err);
        return [];
    }
}